import { useContext } from 'react';
import useThrowAsyncError from '../../../hooks/useThrowAsyncError';
import { deleteFavorite, putFavorite } from '../_api/FavoritesApi';
import FavoritesContext from '../_contexts/FavoritesContext';

const defaultFavorites = {
  medialinks: [],
  userMaterial: [],
  whitepages: [],
};

const typeToKeyMap = {
  'user-material': 'userMaterial',
  whitepages: 'whitepages',
  medialinks: 'medialinks',
} as const;

export default function useFavorites() {
  const handleError = useThrowAsyncError();
  const { favorites, setFavorites } = useContext(FavoritesContext);

  const getFavoritesForModule = (moduleId?: string) => {
    if (!moduleId) return defaultFavorites;

    return favorites[moduleId] || defaultFavorites;
  };

  const toggleFavorite = async (moduleId: string, type: 'user-material' | 'whitepages' | 'medialinks', id: string) => {
    const key = typeToKeyMap[type];

    try {
      const moduleFavorites = getFavoritesForModule(moduleId);

      const updateFavorites = (nextFavorites: string[]) => {
        setFavorites({
          ...favorites,
          [moduleId]: {
            ...moduleFavorites,
            [key]: nextFavorites,
          },
        });
      };

      const favoritesForType = moduleFavorites[key] || [];

      if (favoritesForType.includes(id)) {
        await deleteFavorite(type, moduleId, id);
        updateFavorites(favoritesForType.filter(favoriteId => favoriteId !== id));
      } else {
        await putFavorite(type, moduleId, id);
        updateFavorites([...(moduleFavorites[key] || []), id]);
      }
    } catch (e) {
      handleError(e);
    }
  };

  const favoriteWhitepage = async (moduleId: string, whitepageId: string) =>
    toggleFavorite(moduleId, 'whitepages', whitepageId);

  const favoriteUserMaterial = async (moduleId: string, materialId: string) =>
    toggleFavorite(moduleId, 'user-material', materialId);

  const favoriteMedialink = async (moduleId: string, medialinkId: string) =>
    toggleFavorite(moduleId, 'medialinks', medialinkId);

  return {
    getFavoritesForModule,
    favoriteUserMaterial,
    favoriteWhitepage,
    favoriteMedialink,
  };
}
